/** Cliente de la API del operador de plataforma. */
import type {
  ActualizarEstadoAdministradorRequest,
  ActualizarEstadoAdministradorResponse,
  ActualizarLicenciaRequest,
  ActualizarLicenciaResponse,
  ActualizarSubdominioRequest,
  ActualizarSubdominioResponse,
  CrearAdministradorRequest,
  CrearAdministradorResponse,
  CrearOrganismoRequest,
  CrearOrganismoResponse,
  EditarRolesAdministradorRequest,
  EditarRolesAdministradorResponse,
  ListarAdministradoresResponse,
  ListarOrganismosResponse,
  UploadUrlResponse,
} from '@sumarios/shared';
import { config } from './config.js';
import { sesionActual } from './auth.js';
import type {
  GuardarGestion,
  RegistroGestion,
  TipoGestion,
  CapacidadOperador,
  RolOperador,
  AuditEvent,
  UsuarioOperador,
  CambioOperador,
} from '@sumarios/shared';

async function llamar<T>(
  _idToken: string,
  metodo: string,
  ruta: string,
  cuerpo?: unknown,
): Promise<T> {
  const sesion = await sesionActual();
  if (!sesion) {
    window.dispatchEvent(new Event('operador:sesion-expirada'));
    throw new Error('La sesión terminó. Ingrese nuevamente.');
  }
  const r = await fetch(`${config.apiUrl}${ruta}`, {
    method: metodo,
    headers: {
      authorization: `Bearer ${sesion.idToken}`,
      ...(cuerpo !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(cuerpo !== undefined ? { body: JSON.stringify(cuerpo) } : {}),
  });
  if (!r.ok) {
    if (r.status === 401) window.dispatchEvent(new Event('operador:sesion-expirada'));
    const datos = (await r.json().catch(() => null)) as { mensaje?: string } | null;
    throw new Error(datos?.mensaje ?? 'No se pudo completar la operación.');
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const getMe = (token: string) =>
  llamar<{ operador: { email: string; roles: RolOperador[]; capacidades: CapacidadOperador[] } }>(
    token,
    'GET',
    '/me',
  );
export const listarGestion = (token: string, orgId: string, tipo: TipoGestion) =>
  llamar<{ registros: RegistroGestion[] }>(
    token,
    'GET',
    `/organismos/${encodeURIComponent(orgId)}/gestion/${tipo}`,
  );
export const guardarGestion = (
  token: string,
  orgId: string,
  tipo: TipoGestion,
  datos: GuardarGestion,
) =>
  llamar<{ registro: RegistroGestion }>(
    token,
    'POST',
    `/organismos/${encodeURIComponent(orgId)}/gestion/${tipo}`,
    datos,
  );
export const uploadGestion = (
  token: string,
  orgId: string,
  tipo: TipoGestion,
  id: string,
  tamanoBytes: number,
) =>
  llamar<{ uploadUrl: string; s3Key: string }>(
    token,
    'POST',
    `/organismos/${encodeURIComponent(orgId)}/gestion/${tipo}/${id}/upload-url`,
    { contentType: 'application/pdf', tamanoBytes },
  );
export const descargarGestion = (token: string, registro: RegistroGestion, indice: number) =>
  llamar<{ downloadUrl: string }>(
    token,
    'POST',
    `/organismos/${encodeURIComponent(registro.orgId)}/gestion/${registro.tipo}/${registro.registroId}/documentos/${indice}`,
  );
export const auditGestion = (token: string, orgId: string) =>
  llamar<{ eventos: AuditEvent[] }>(token, 'GET', `/organismos/${encodeURIComponent(orgId)}/audit`);

/** Sube el archivo directo al bucket usando la URL prefirmada (no lleva el JWT de la API). */
export async function subirArchivo(uploadUrl: string, archivo: File): Promise<void> {
  const r = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': archivo.type },
    body: archivo,
  });
  if (!r.ok) throw new Error(`Subida a S3 falló: ${r.status}`);
}

export const listarOrganismos = (idToken: string) =>
  llamar<ListarOrganismosResponse>(idToken, 'GET', '/organismos');

export const crearOrganismo = (idToken: string, datos: CrearOrganismoRequest) =>
  llamar<CrearOrganismoResponse>(idToken, 'POST', '/organismos', datos);

export const actualizarLicencia = (
  idToken: string,
  orgId: string,
  datos: ActualizarLicenciaRequest,
) => llamar<ActualizarLicenciaResponse>(idToken, 'PATCH', `/organismos/${orgId}/licencia`, datos);

export const actualizarSubdominio = (
  idToken: string,
  orgId: string,
  datos: ActualizarSubdominioRequest,
) =>
  llamar<ActualizarSubdominioResponse>(idToken, 'PATCH', `/organismos/${orgId}/subdominio`, datos);

export const listarAdministradores = (idToken: string, orgId: string) =>
  llamar<ListarAdministradoresResponse>(idToken, 'GET', `/organismos/${orgId}/administradores`);

/** Sin orgId: sirve tanto para el alta de organismo (primer admin) como para admins/auditores de uno existente. */
export const solicitarUploadUrlEvidencia = (
  idToken: string,
  contentType: string,
  tamanoBytes: number,
) =>
  llamar<UploadUrlResponse>(idToken, 'POST', '/evidencia/upload-url', {
    contentType,
    tamanoBytes,
  });

export const crearAdministrador = (
  idToken: string,
  orgId: string,
  datos: CrearAdministradorRequest,
) =>
  llamar<CrearAdministradorResponse>(
    idToken,
    'POST',
    `/organismos/${orgId}/administradores`,
    datos,
  );
export const historialGestion = (token: string, registro: RegistroGestion) =>
  llamar<{ versiones: RegistroGestion[] }>(
    token,
    'GET',
    `/organismos/${encodeURIComponent(registro.orgId)}/gestion/${registro.tipo}/${registro.registroId}/historial`,
  );

export const editarRolesAdministrador = (
  idToken: string,
  orgId: string,
  userId: string,
  datos: EditarRolesAdministradorRequest,
) =>
  llamar<EditarRolesAdministradorResponse>(
    idToken,
    'PATCH',
    `/organismos/${orgId}/administradores/${userId}/roles`,
    datos,
  );

export const actualizarEstadoAdministrador = (
  idToken: string,
  orgId: string,
  userId: string,
  datos: ActualizarEstadoAdministradorRequest,
) =>
  llamar<ActualizarEstadoAdministradorResponse>(
    idToken,
    'PATCH',
    `/organismos/${orgId}/administradores/${userId}/estado`,
    datos,
  );

export const listarEquipo = (token: string, cursor?: string) =>
  llamar<{ usuarios: UsuarioOperador[]; cursor?: string }>(
    token,
    'GET',
    `/operadores${cursor ? '?cursor=' + encodeURIComponent(cursor) : ''}`,
  );
export const crearOperador = (token: string, datos: CambioOperador) =>
  llamar(token, 'POST', '/operadores', datos);
export const cambiarOperador = (
  token: string,
  id: string,
  accion: 'roles' | 'estado' | 'invitacion',
  datos: CambioOperador,
) =>
  llamar(
    token,
    accion === 'invitacion' ? 'POST' : 'PATCH',
    `/operadores/${encodeURIComponent(id)}/${accion}`,
    datos,
  );
export const auditEquipo = (token: string) =>
  llamar<{ eventos: AuditEvent[] }>(token, 'GET', '/operadores/audit');
